import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { reportPostSchema } from '../../../forms/schemas/report_post'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { theme } from '../../../theme/theme'
import { Modal } from '../../common/Modal'
import { ModalBody } from '../../common/Modal/ModalBody'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import { ToastType } from '../../common/Toast/toast-type'
import { ReportPostModalContent } from './ReportPostModalContent'
import { report } from '../../../api/posts'

interface ReportPostModalProps {
  show: boolean
  close: () => void
  postId: string
}

export const ReportPostModal = ({
  show,
  close,
  postId,
}: ReportPostModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const { mutate: mutateReportPost, isPending: isReportingPost } = useMutation({
    mutationFn: report,
    onSuccess: () => {
      close()
      setToastNotification(
        ToastType.SUCCESS,
        translate('reportPost.flashMessage.reportSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('reportPost.flashMessage.reportFailure')
      )
    },
  })

  const onSubmit = async (
    data: InferType<typeof reportPostSchema>
  ): Promise<void> => {
    mutateReportPost({ id: postId, body: { message: data.message } })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isReportingPost

  return (
    <Modal show={show} size="md">
      {{
        header: (
          <ModalHeader flexItems="items-start" onClose={close}>
            <div className="flex flex-col gap-2">
              {translate('reportPost.title')}
              <p style={theme.textVariants.base}>
                {translate('reportPost.subtitle')}
              </p>
            </div>
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <ReportPostModalContent
              onError={onError}
              onSubmit={onSubmit}
              onClose={close}
              loading={loading}
            />
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
